import { Fragment, useCallback, useContext, useState } from "react";
import SubHeader, { SubHeaderLeft, SubHeaderRight, SubheaderSeparator } from "../../../layout/SubHeader/SubHeader";
import Card, { CardBody, CardTitle } from "../../../components/bootstrap/Card";
import Button from "../../../components/bootstrap/Button";
import Page from "../../../layout/Page/Page";
import { handleConfirmationAlert } from "../../../utils/ConfirmationAlert";
import ErrorMessage from "../../../components/ErrorMessage";
import { CustomTable } from "../../../components/table/CustomTable";
import { toast } from "react-toastify";
import useFetch from "../../../hooks/useFetch";
import useFilters from "../../../hooks/useFilters";
import { PrivilegeContext } from "../../../components/priviledge/PriviledgeProvider";
import { IncomesApiResponse } from "../../../type/income-type";
import { IncomeService } from "../../../services/incomes/incomeService";
import CreateModalIncome from "../income-create/createModal";
import IncomeFilters from "./income-options/IncomeFilters";
import moment from "moment";
import { FinancesMenu } from "../../../menu";
import { changeProjectActiveTab } from "../../../redux/projectSlice";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FixNumber } from "../../../utils/fixNumber";

export interface IIncomeFilters {
    active?: number;
}

const incomeFilters: IIncomeFilters = {
    active: 1
};

const IncomeList = () => {

    const { userCan } = useContext(PrivilegeContext);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<any>(null);
    const [show, setShow] = useState(false);
    const incomeService = new IncomeService();

    const { filters, updateFilters, resetFilters, updateFilterOrder, updatePage, updatePageSize } = useFilters(incomeFilters);

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        const response = await incomeService.getIncomes(filters);
        return response.getResponseData() as IncomesApiResponse;
    }, [filters]));

    const handleDelete = async (id: string) => {
        try {
            const response = await (await (new IncomeService()).deleteIncome(id)).getResponseData();
            if (response.success) {
                refetch();
                setTimeout(() => {
                    toast.success('Ingreso eliminado correctamente');
                }, 100);
            } else {
                toast.error(response.message || "Error al eliminar el ingreso");
            }
        } catch (error: any) {
            toast.error(error.message);
        }
    };

    const _onCloseModal = (type: number, message?: string, errors?: any) => {
        if (type === 1) {
            setSelectedItem(null);
            setShow(false);
            setModalOpen(false);
            refetch();
            toast.success(message);
        } else if (type === 0) {
            message !== '' && toast.error(message);
            errors && errors.forEach((error: any) => {
                toast.error(error.message);
            });
        }
    };

    const getContent = () => {
        if (error) return <ErrorMessage error={error} />;

        if (data) {
            let apiData = data as IncomesApiResponse;

            return (
                <CustomTable
                    title="Ingresos"
                    data={apiData ? apiData.income : null}
                    pagination={true}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: apiData as IncomesApiResponse ? apiData.lastPage : 1,
                        handlePagination: (page: any) => {
                            updatePage({ selected: page.selected + 1 });
                        },
                        handlePerPage: updatePageSize,
                    }}
                    className={"table-striped table-hover"}
                    columns={[
                        {
                            name: 'Proyecto',
                            keyValue: 'project',
                            className: (userCan("list", "project") ? "text-center" : "d-none"),
                            isVisible: (userCan("list", "project")),
                            sortable: true,
                            sortColumn: updateFilterOrder,
                            render: (element: any) => {
                                return (
                                    element.stages?.budget
                                        ? (
                                            <span className="text-nowrap">
                                                {`${element.stages.budget.address} (${element.stages.budget.name})`}
                                            </span>
                                        )
                                        : <div className="text-center">-</div>
                                );
                            },
                        },
                        {
                            name: "Concepto",
                            keyValue: "concept",
                            className: "text-center",
                            sortable: true,
                            sortColumn: updateFilterOrder,
                            render: (element: any) => {
                                return (
                                    <div className="cursor-pointer text-primary fw-bold text-center" onClick={() => {
                                        setSelectedItem(element);
                                        setModalOpen(true);
                                        setShow(true);
                                    }}>
                                        {element.concept || "-"}
                                    </div>
                                )
                            },

                        },
                        {
                            name: "Importe",
                            keyValue: "price",
                            className: "text-center",
                            sortable: true,
                            sortColumn: updateFilterOrder,
                            render: (element: any) => {
                                return (
                                    <div className="d-flex justify-content-center">
                                        {FixNumber(element.price) || "-"}€
                                    </div>
                                );
                            },
                        },
                        {
                            name: "Descripción",
                            keyValue: "description",
                            className: "text-center",
                            render: (element: any) => {
                                return (
                                    <div className="d-flex justify-content-center">
                                        <div className="text-start">
                                            {element.description || "-"}
                                        </div>
                                    </div>
                                );
                            },
                        },
                        {
                            name: "Fecha Estimada",
                            keyValue: "estimated_entry_date",
                            sortable: true,
                            sortColumn: updateFilterOrder,
                            render: (element: any) => {
                                return (
                                    <>
                                        {moment(element.estimatedEntryDate.date).format('DD/MM/YY')}
                                    </>
                                )
                            },
                        },
                        {
                            name: "Fecha Ingreso",
                            keyValue: "effective_entry_date",
                            sortable: true,
                            sortColumn: updateFilterOrder,
                            render: (element: any) => {
                                return (
                                    <>
                                        {element.effectiveEntryDate ? moment(element.effectiveEntryDate.date).format('DD/MM/YY') : '-'}
                                    </>
                                )
                            },
                        },
                        {
                            name: "Fecha Creación",
                            keyValue: "created_at",
                            sortable: true,
                            sortColumn: updateFilterOrder,
                            render: (element: any) => {
                                return (
                                    <>
                                        {moment(element.createdAt.date).format('DD/MM/YY')}
                                    </>
                                )
                            },

                        },
                        {
                            name: "Estado",
                            keyValue: "paid",
                            className: "text-center",
                            render: (element: any) => {
                                return (
                                    <div className={`d-flex justify-content-center py-2 badge ${!element.paid ? 'bg-primary' : 'bg-success'}`} style={{ width: 'fit-content' }}>
                                        {element.paid ? 'Pagado' : 'Sin pagar'}
                                    </div>
                                );
                            },
                        },
                        {
                            name: "Tipo",
                            keyValue: "income_type",
                            className: "text-center",
                            sortable: true,
                            sortColumn: updateFilterOrder,
                            render: (element: any) => {
                                return (
                                    <div className="d-flex justify-content-center">
                                        {element.incomeType.name || "-"}
                                    </div>
                                );
                            },
                        },
                        {
                            name: "Impuestos",
                            keyValue: "taxes",
                            className: "text-center",
                            sortable: true,
                            sortColumn: updateFilterOrder,
                            render: (element: any) => {
                                if (element.taxes && element.taxes?.name) {
                                    return (
                                        <div className="d-flex justify-content-center">
                                            <span className="text-muted">{element.taxes.name || "-"}:&nbsp;</span> {element.taxes.value || "0"}%
                                        </div>
                                    );
                                }
                                return (
                                    <div className="d-flex justify-content-center">
                                        {element.taxes.map((tax: any, index: number) => {
                                            return (
                                                <div key={tax.id + index} className="d-flex flex-column flex-nowrap justify-content-center">
                                                    <div>
                                                        <span className="text-muted">{tax.name || "-"}:&nbsp;</span>
                                                        {tax.value || "0"}%
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            },
                        },
                        {
                            name: "Organización",
                            keyValue: "company",
                            className: (userCan("list", "companies") ? "text-center" : "d-none"),
                            isVisible: (userCan("list", "companies") ? true : false),
                            render: (element: any) => {
                                return (
                                    <div className="d-flex justify-content-center">
                                        {element.company.name || "-"}
                                    </div>
                                );
                            },
                        },
                        { name: "Acciones", className: "min-w-100px text-end", isActionCell: true }
                    ]}
                    actions={[
                        {
                            title: "Editar",
                            buttonType: 'icon',
                            icon: "Edit",
                            additionalClasses: `text-primary ${userCan("edit", "income") ? "" : "d-none"}`,
                            description: "Editar ingreso",
                            callback: (item: any) => {
                                if (!userCan("edit", "income")) return;
                                setSelectedItem(item);
                                setModalOpen(true);
                                setShow(false);
                            },
                        },
                        {
                            title: "Eliminar",
                            buttonType: 'icon',
                            icon: "Delete",
                            additionalClasses: `text-danger ${userCan("delete", "income") ? "" : "d-none"}`,
                            description: "Eliminar Ingreso",
                            callback: (item: any) => {
                                if (!userCan("delete", "income")) return;
                                handleConfirmationAlert({
                                    title: "Eliminar Ingreso",
                                    text: "¿Está seguro que desea eliminar el ingreso?",
                                    icon: "warning",
                                    onConfirm: () => {
                                        handleDelete(item.id);
                                    }
                                })
                            },
                        },
                        {
                            title: "Sin permisos",
                            buttonType: 'icon',
                            icon: "Block",
                            additionalClasses: `text-danger ${(userCan('edit', 'income') || userCan('delete', 'income')) ? 'd-none' : ''}`,
                            description: "No tiene permisos para editar ni eliminar",
                            callback: (item: any) => { },
                        }
                    ]}
                />
            );
        }
    };

    return (
        <Fragment>
            <SubHeader>
                <SubHeaderLeft>
                    <CardTitle>Listado de Ingresos</CardTitle>
                    <SubheaderSeparator />
                    <Button
                        color="primary"
                        title="Crear Ingreso"
                        icon="Add"
                        isLight
                        className={userCan("create", "income") ? "" : "d-none"}
                        onClick={() => {
                            if (!userCan("create", "income")) return;
                            setModalOpen(true);
                            setSelectedItem(null);
                            setShow(false);
                        }}
                    />
                </SubHeaderLeft>
                <SubHeaderRight>
                    <IncomeFilters filters={filters} updateFilters={updateFilters} updatePageSize={updatePageSize} resetFilters={resetFilters} />
                </SubHeaderRight>
            </SubHeader>
            <Page container="fluid">
                <Card>
                    <CardBody className="table-responsive">
                        <Fragment>{getContent()}</Fragment>
                    </CardBody>
                </Card>
            </Page>
            {modalOpen && (<CreateModalIncome isOpen={modalOpen} setIsOpen={setModalOpen} onClose={_onCloseModal} data={selectedItem} show={show} />)}
        </Fragment>
    );
};

export default IncomeList;